import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'src/axios';
import { APICONSTANTS } from 'src/components/Constants';
import { toDiseaseMeta } from './diseaseMeta';

// Reference metadata for every disease on the pedigree, keyed by disease id.
//
// The analyzer is pure and does no I/O, so the metadata that decides whether a
// Mendelian mode of inheritance may be asserted has to be fetched out here and
// handed in. One batched request per distinct disease set, not one per disease.
//
// FAILURE BEHAVIOUR IS PART OF THE SAFETY ARGUMENT. When the lookup is
// unavailable, disabled by an admin, or simply has nothing for a disease, the
// map has no entry for it — and `canAssertMendelianMode` reads a missing entry
// as "assert no mode". So degradation moves the panel toward stating
// observations without claiming mechanisms. It never falls back to guessing
// one, and it never blocks the rest of the analysis.

const EMPTY = Object.freeze(new Map());

// Diagnoses carry a clinician-recorded inheritance mode that overrides the
// reference in both directions; it lives on the diagnosis, not the disease, so
// it has to be collected from the pedigree rather than fetched.
const collectDiseases = (members) => {
  const byId = new Map();
  for (const m of members || []) {
    for (const d of m?.diagnoses || []) {
      if (!d?.id) continue;
      const existing = byId.get(d.id);
      // First non-empty value wins for each field. A clinician mode recorded
      // on ANY member's copy of the diagnosis applies to the disease — the
      // field is a statement about the condition, not about that relative.
      byId.set(d.id, {
        id: d.id,
        name: existing?.name || d.name || '',
        clinicianMode: existing?.clinicianMode || d.inheritanceMode || null
      });
    }
  }
  return byId;
};

export function useDiseaseMeta(members, { enabled = true } = {}) {
  const [byId, setById] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  // Guards against a late response from a previous pedigree overwriting the
  // current one — a pedigree tab switch is fast and the request is not.
  const requestSeq = useRef(0);

  const diseases = useMemo(() => collectDiseases(members), [members]);

  // Stable identity for the disease set, so the effect fires when the set
  // changes and not on every render of the same set.
  const key = useMemo(
    () =>
      [...diseases.values()]
        .map((d) => `${d.id}|${d.clinicianMode || ''}`)
        .sort()
        .join(';'),
    [diseases]
  );

  useEffect(() => {
    if (!enabled || diseases.size === 0) {
      setById(EMPTY);
      setLoading(false);
      return;
    }

    const seq = ++requestSeq.current;
    let cancelled = false;
    setLoading(true);

    axios
      .post(APICONSTANTS.diseaseInheritance, {
        ids: [...diseases.keys()]
      })
      .then(({ data }) => {
        if (cancelled || seq !== requestSeq.current) return;
        const next = new Map();
        for (const entry of data?.results || []) {
          const local = diseases.get(entry.input) || diseases.get(entry.id);
          next.set(
            local?.id || entry.id,
            toDiseaseMeta(entry, {
              name: local?.name || entry.name,
              clinicianMode: local?.clinicianMode
            })
          );
        }
        // A disease the reference did not resolve still needs an entry, so the
        // clinician's own recorded mode can override an absent reference.
        for (const [id, local] of diseases) {
          if (next.has(id)) continue;
          next.set(id, toDiseaseMeta(null, local));
        }
        setById(next);
      })
      .catch(() => {
        if (cancelled || seq !== requestSeq.current) return;
        // Still publish the clinician-recorded values: their judgement should
        // survive a terminology outage.
        const next = new Map();
        for (const [id, local] of diseases) {
          next.set(id, toDiseaseMeta(null, local));
        }
        setById(next);
      })
      .finally(() => {
        if (!cancelled && seq === requestSeq.current) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [key, enabled, diseases]);

  return { diseaseMeta: byId, loading };
}
